import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, map, merge, Observable, of, scan, Subject } from 'rxjs';
import { InformationMessage } from 'src/app/interfaces';
import AppSettings from '../../app.settings';

export interface Toast {
  delay: number;
  message: InformationMessage;
}

type Operation = (value: Toast) => State;
type State = (state: Toast[]) => Toast[];

// array operation
const add: Operation = (t: Toast) => (s: Toast[]) => [...s, t];
const remove: Operation = (t: Toast) => (s: Toast[]) => s.filter((v) => v !== t);
const clearAll: () => State = () => (_s: Toast[]) => [];

@Injectable({ providedIn: 'root' })
export class ToastMessageService {
  private readonly addToast$: Subject<Toast> = new Subject<Toast>();
  private readonly removeToast$: Subject<Toast> = new Subject<Toast>();
  private readonly clearAllToasts$: Subject<void> = new Subject<void>();

  private readonly routeChanges$ = inject(Router).events.pipe(
    // we need to listen only events that triggered by the browser next/back buttons
    filter((event) => event instanceof NavigationStart && event.navigationTrigger === 'popstate'),
    map(() => of(void 0)),
  );

  public readonly toasts$: Observable<Toast[]> = merge(
    this.addToast$.pipe(map(add)),
    this.removeToast$.pipe(map(remove)),
    this.routeChanges$.pipe(map(clearAll)),
    this.clearAllToasts$.pipe(map(clearAll)),
  ).pipe(scan((state: Toast[], operation: State) => operation(state), []));

  public remove(toast: Toast): void {
    this.removeToast$.next(toast);
  }

  public clearAll() {
    this.clearAllToasts$.next();
  }

  public showFailureMessage(message: InformationMessage) {
    this.addToast$.next({ delay: AppSettings.FAILURE_TOAST_DELAY, message });
  }

  public showSuccessfulMessage(message: string) {
    this.addToast$.next({
      delay: AppSettings.DEFAULT_TOAST_DELAY,
      message: { title: '', content: [{ type: 'text', value: message }], level: 'SUCCESS', replies: [] },
    });
  }
}
